/**
 * Refs:
 * https://codesandbox.io/s/l3194?file=/src/App.js:1326-1450
 * https://github.com/ampproject/amphtml/blob/main/docs/spec/amp-story-player.md
 * https://github.com/ampproject/amphtml/blob/main/src/amp-story-player/amp-story-player-impl.js
 * https://github.com/ampproject/amphtml/issues/24539
 */

import React, { FC, useEffect, useRef, useMemo } from 'react';
import { useScript } from '../../utils/hooks';
import { AmpStoryPlayerProps, PlayerInstance } from './ampStoryPlayer.types';
import { getAmpCss } from './utils';

const AMP_SCRIPT = 'https://cdn.ampproject.org/amp-story-player-v0.js';

export const AmpStoryPlayer: FC<AmpStoryPlayerProps> = ({
  children,
  className,
  config,
  onPlayerReady,
  stories = [],
  ...restProps
}): JSX.Element => {
  const playerRef = useRef<PlayerInstance>();
  const ampScriptStatus = useScript(AMP_SCRIPT);
  const isMuted = useRef();

  useEffect(() => {
    if (!document.getElementById('amp-player-css')) {
      document.head.appendChild(getAmpCss());
    }
  }, []);

  useEffect(() => {
    const player = playerRef?.current;

    if (!player || ampScriptStatus !== 'ready' || !window.AmpStoryPlayer) {
      return;
    }

    const onReadyHandler = () => {
      const shadowRoot = document?.querySelector(
        '.i-amphtml-story-player-shadow-root-intermediary'
      )?.shadowRoot;

      if (shadowRoot) {
        const style = document.createElement('style');

        /*
          These style additions are meant to address the following issues:
          * Safari workaround for navigation buttons not showing: https://github.com/ampproject/amphtml/issues/37211
          * Fix exit-control button not visible
        */
        style.innerHTML = `
          .i-amphtml-story-player-main-container iframe:nth-of-type(1),
          .i-amphtml-story-player-main-container .story-player-iframe[i-amphtml-iframe-position="0"] {
            transform: translate3d(0, 0, 0);
          }
          .i-amphtml-story-player-main-container .amp-story-player-exit-control-button {
            transform: translateZ(2px);
          }
        `;

        shadowRoot.appendChild(style);
      }

      return onPlayerReady && onPlayerReady(player);
    };

    // Story switching
    const navigationHandler = () => {
      if (
        typeof isMuted.current === 'undefined' ||
        typeof player?.unmute !== 'function' ||
        typeof player?.mute !== 'function'
      ) {
        return;
      }
      player[isMuted.current ? 'mute' : 'unmute']();
    };

    // Story mute/unmute button click
    const onAmpStoryMutedStateHandler = (event: any) => {
      isMuted.current = event.detail.muted;
    };

    // Load only once
    if (!player.isReady) {
      new window.AmpStoryPlayer(window, player).load();
    }

    // If player is ready after calling `.load()` above
    if (player.isReady) {
      onReadyHandler();
    } else {
      // if player is not ready yet, add event listener
      player.addEventListener('ready', onReadyHandler);
    }

    // Triggers on Story Switch (only when there are more than one stories in the player)
    player.addEventListener('navigation', navigationHandler);
    // Triggers on Story mute/unmute button click
    player.addEventListener(
      'amp-story-muted-state',
      onAmpStoryMutedStateHandler
    );

    return () => {
      player.removeEventListener('ready', onReadyHandler);
      player.removeEventListener('navigation', navigationHandler);
      player.removeEventListener(
        'amp-story-muted-state',
        onAmpStoryMutedStateHandler
      );
    };
  }, [ampScriptStatus, onPlayerReady]);

  const renderStories = useMemo(
    () =>
      stories?.reduce(
        (arr: JSX.Element[], { href, title, posterImage }, index) => {
          if (href) {
            arr.push(
              <a href={href} key={`${href}-${index}`}>
                {posterImage && (
                  <img
                    src={posterImage}
                    loading='lazy'
                    data-amp-story-player-poster-img
                  />
                )}
                {title}
              </a>
            );
          }
          return arr;
        },
        []
      ),
    [stories]
  );

  return (
    <>
      <amp-story-player class={className} ref={playerRef} {...restProps}>
        {config && (
          <script type='application/json'>
            {JSON.stringify({ ...config })}
          </script>
        )}

        {renderStories}
        {children}
      </amp-story-player>
    </>
  );
};

export default AmpStoryPlayer;
